import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Search,
  Plus,
  Edit,
  BanIcon,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  BarChart2,
  Tag,
  Percent,
  DollarSign,
  Truck,
  Eye,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { couponsApi } from "../utils/api";

// ─── Types ───────────────────────────────────────────────────────────────────

type CouponType = "PERCENTAGE" | "FIXED" | "FREE_SHIPPING";
type StatusFilter = "all" | "active" | "inactive";
type TypeFilter = "all" | CouponType;

interface Coupon {
  _id: string;
  code: string;
  type: CouponType;
  value: number;
  minOrderAmount: number;
  maxDiscount: number | null;
  usageLimit: number | null;
  usagePerUser: number | null;
  startDate: string;
  endDate: string;
  isActive: boolean;
  applicableProducts: string[];
  applicableCategories: string[];
  usageCount: number;
  createdAt?: string;
  updatedAt?: string;
}

interface CouponUsageRecord {
  _id: string;
  couponId: string;
  userId: { _id: string; email?: string; name?: string } | string;
  orderId: { _id: string } | string;
  usageCount: number;
  createdAt?: string;
}

interface CouponFormState {
  code: string;
  type: CouponType;
  value: number;
  minOrderAmount: number;
  maxDiscount: string;
  usageLimit: string;
  usagePerUser: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  /** Comma / newline separated ObjectId strings */
  applicableProductsText: string;
  applicableCategoriasText: string;
}

const DEFAULT_FORM: CouponFormState = {
  code: "",
  type: "PERCENTAGE",
  value: 0,
  minOrderAmount: 0,
  maxDiscount: "",
  usageLimit: "",
  usagePerUser: "",
  startDate: "",
  endDate: "",
  isActive: true,
  applicableProductsText: "",
  applicableCategoriasText: "",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Parse a block of text (newline or comma separated) into an array of non-empty strings */
const parseIds = (text: string): string[] =>
  text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const toISODateInput = (iso: string) => {
  if (!iso) return "";
  try {
    return new Date(iso).toISOString().slice(0, 16);
  } catch {
    return "";
  }
};

const toFormState = (c: Coupon): CouponFormState => ({
  code: c.code,
  type: c.type,
  value: c.value,
  minOrderAmount: c.minOrderAmount,
  maxDiscount: c.maxDiscount != null ? String(c.maxDiscount) : "",
  usageLimit: c.usageLimit != null ? String(c.usageLimit) : "",
  usagePerUser: c.usagePerUser != null ? String(c.usagePerUser) : "",
  startDate: toISODateInput(c.startDate),
  endDate: toISODateInput(c.endDate),
  isActive: c.isActive,
  applicableProductsText: (c.applicableProducts ?? []).join("\n"),
  applicableCategoriasText: (c.applicableCategories ?? []).join("\n"),
});

/**
 * Robustly extract the coupons array from whatever shape the API returns.
 * Priority: data.items → data.docs → data.coupons → data.data → data (if array) → res (if array)
 */
const extractCoupons = (res: any): Coupon[] => {
  if (!res) return [];
  const d = res.data;
  if (d && Array.isArray(d.items)) return d.items;
  if (d && Array.isArray(d.docs)) return d.docs;
  if (d && Array.isArray(d.coupons)) return d.coupons;
  if (d && Array.isArray(d.data)) return d.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(res)) return res;
  return [];
};

const extractPagination = (res: any, limitFallback: number = 10) => {
  const d = res?.data ?? res ?? {};
  const total = d.totalDocs ?? d.total ?? d.count ?? 0;
  const totalPages =
    d.totalPages ?? d.pages ?? (total > 0 ? Math.ceil(total / (d.limit ?? limitFallback)) : 0);
  return { totalPages, total };
};

const typeIcon = (type: CouponType) => {
  switch (type) {
    case "PERCENTAGE":
      return <Percent className="w-3 h-3" />;
    case "FIXED":
      return <DollarSign className="w-3 h-3" />;
    case "FREE_SHIPPING":
      return <Truck className="w-3 h-3" />;
  }
};

const typeBadgeColor = (type: CouponType) => {
  switch (type) {
    case "PERCENTAGE":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "FIXED":
      return "bg-green-100 text-green-700 border-green-200";
    case "FREE_SHIPPING":
      return "bg-purple-100 text-purple-700 border-purple-200";
  }
};

const formatDate = (iso: string) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

const formatDateTime = (iso?: string) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const isExpired = (endDate: string) => new Date(endDate) < new Date();

// ─── ID Tag pill ──────────────────────────────────────────────────────────────

function IdTagList({
  ids,
  label,
}: {
  ids: string[];
  label: string;
}) {
  if (!ids || ids.length === 0) return <span className="text-muted-foreground text-xs">All {label}</span>;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {ids.slice(0, 3).map((id) => (
        <span
          key={id}
          className="inline-block font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded border"
        >
          {id.slice(-8)}
        </span>
      ))}
      {ids.length > 3 && (
        <span className="text-xs text-muted-foreground">+{ids.length - 3} more</span>
      )}
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function PaginationBar({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

  const pages: React.ReactNode[] = [];
  if (start > 1)
    pages.push(
      <Button key="first" variant="outline" size="sm" onClick={() => onPageChange(1)} disabled={currentPage === 1}>
        <ChevronsLeft className="w-4 h-4" />
      </Button>
    );
  pages.push(
    <Button key="prev" variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
      <ChevronLeft className="w-4 h-4" />
    </Button>
  );
  for (let i = start; i <= end; i++) {
    pages.push(
      <Button key={i} variant={currentPage === i ? "default" : "outline"} size="sm" onClick={() => onPageChange(i)}>
        {i}
      </Button>
    );
  }
  pages.push(
    <Button key="next" variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
      <ChevronRight className="w-4 h-4" />
    </Button>
  );
  if (end < totalPages)
    pages.push(
      <Button key="last" variant="outline" size="sm" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages}>
        <ChevronsRight className="w-4 h-4" />
      </Button>
    );
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
      <p className="text-sm text-muted-foreground">
        Page {currentPage} of {totalPages}
      </p>
      <div className="flex items-center gap-2">{pages}</div>
    </div>
  );
}

// ─── Usage Modal ──────────────────────────────────────────────────────────────

function UsageModal({
  coupon,
  onClose,
}: {
  coupon: Coupon;
  onClose: () => void;
}) {
  const [usageRecords, setUsageRecords] = useState<CouponUsageRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await couponsApi.getCouponUsage(coupon._id, page, limit);
      const docs: CouponUsageRecord[] =
        (res as any)?.data?.docs ??
        (res as any)?.data?.usageRecords ??
        (res as any)?.data?.data ??
        [];
      setUsageRecords(docs);
      setTotalPages((res as any)?.data?.totalPages ?? 0);
      setTotal(
        (res as any)?.data?.totalDocs ??
          (res as any)?.data?.total ??
          docs.length
      );
    } catch (err) {
      console.error("Coupon usage fetch error:", err);
      toast.error("Failed to load usage records");
      setUsageRecords([]);
    } finally {
      setLoading(false);
    }
  }, [coupon._id, page]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const resolveId = (ref: { _id: string } | string) =>
    typeof ref === "string" ? ref : ref._id;

  const resolveEmail = (ref: { _id: string; email?: string; name?: string } | string) => {
    if (typeof ref === "string") return ref.slice(0, 12) + "…";
    return ref.email ?? ref.name ?? ref._id.slice(0, 12) + "…";
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-primary" />
            Usage — <span className="font-mono">{coupon.code}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Summary strip */}
        <div className="grid grid-cols-3 gap-3 mb-2">
          {[
            { label: "Total Usage", value: coupon.usageCount },
            { label: "Usage Limit", value: coupon.usageLimit ?? "Unlimited" },
            { label: "Records Found", value: total },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border bg-muted/30 p-3 text-center">
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="py-10 text-center text-muted-foreground">Loading usage records…</div>
        ) : usageRecords.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">No usage records found</div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Count</TableHead>
                  <TableHead>Used At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usageRecords.map((r, idx) => (
                  <TableRow key={r._id}>
                    <TableCell className="text-muted-foreground">{(page - 1) * limit + idx + 1}</TableCell>
                    <TableCell className="font-mono text-xs">{resolveEmail(r.userId as any)}</TableCell>
                    <TableCell className="font-mono text-xs">{resolveId(r.orderId as any).slice(-8)}</TableCell>
                    <TableCell>{r.usageCount}</TableCell>
                    <TableCell className="text-xs">{formatDateTime(r.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationBar currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── ID List Editor ───────────────────────────────────────────────────────────

/**
 * Tag-style input for a list of MongoDB ObjectIds.
 * Displays existing IDs as pills and lets the user paste a raw ObjectId to add it.
 */
function IdListEditor({
  label,
  hint,
  textValue,
  onChange,
}: {
  label: string;
  hint: string;
  textValue: string;
  onChange: (newText: string) => void;
}) {
  const ids = parseIds(textValue);

  const removeId = (id: string) => {
    const next = ids.filter((i) => i !== id).join("\n");
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {/* Pills */}
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 rounded-md border border-gray-200 bg-muted/30 min-h-[36px]">
          {ids.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 font-mono text-xs bg-background border rounded px-1.5 py-0.5"
            >
              {id.length > 10 ? `…${id.slice(-8)}` : id}
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive transition-colors"
                onClick={() => removeId(id)}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Textarea for raw input */}
      <Textarea
        value={textValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder={hint}
        rows={3}
        className="border border-gray-300 font-mono text-xs"
      />
      <p className="text-xs text-muted-foreground">
        Enter one ObjectId per line (or comma-separated). Leave blank to apply to <strong>all</strong>.
      </p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function Coupons() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [formData, setFormData] = useState<CouponFormState>(DEFAULT_FORM);
  const [actionLoadingIds, setActionLoadingIds] = useState<Set<string>>(new Set());

  const [usageModalCoupon, setUsageModalCoupon] = useState<Coupon | null>(null);
  const [viewCoupon, setViewCoupon] = useState<Coupon | null>(null);

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const isActive =
        statusFilter === "all" ? undefined : statusFilter === "active";
      const res = await couponsApi.listCoupons(
        currentPage,
        limit,
        searchTerm,
        isActive,
        typeFilter === "all" ? undefined : typeFilter
      );

      console.log("[Coupons] API response:", res); // Debug — remove after confirming

      const docs = extractCoupons(res);
      const { totalPages: tp, total: t } = extractPagination(res, limit);

      setCoupons(docs);
      setTotalPages(tp);
      setTotal(t);
    } catch (err) {
      console.error("[Coupons] Fetch error:", err);
      const msg = err instanceof Error ? err.message : "Failed to fetch coupons";
      setFetchError(msg);
      toast.error(msg);
      setCoupons([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  // ── Filter handlers ────────────────────────────────────────────────────────

  const handleSearch = (v: string) => { setSearchTerm(v); setCurrentPage(1); };
  const handleStatusChange = (v: StatusFilter) => { setStatusFilter(v); setCurrentPage(1); };
  const handleTypeChange = (v: TypeFilter) => { setTypeFilter(v); setCurrentPage(1); };
  const handleLimitChange = (v: string) => { setLimit(Number(v)); setCurrentPage(1); };

  // ── Form helpers ───────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingCoupon(null);
    setFormData(DEFAULT_FORM);
    setIsFormOpen(true);
  };

  const openEdit = (c: Coupon) => {
    setEditingCoupon(c);
    setFormData(toFormState(c));
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (isSubmitting) return;
    setIsFormOpen(false);
    setEditingCoupon(null);
    setFormData(DEFAULT_FORM);
  };

  const setField = <K extends keyof CouponFormState>(key: K) =>
    (value: CouponFormState[K]) =>
      setFormData((prev) => ({ ...prev, [key]: value }));

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    if (!formData.code.trim()) { toast.error("Coupon code is required"); return false; }
    if (formData.type !== "FREE_SHIPPING" && formData.value <= 0) {
      toast.error("Value must be greater than 0"); return false;
    }
    if (formData.type === "PERCENTAGE" && formData.value > 100) {
      toast.error("Percentage value cannot exceed 100"); return false;
    }
    if (!formData.startDate) { toast.error("Start date is required"); return false; }
    if (!formData.endDate) { toast.error("End date is required"); return false; }
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      toast.error("End date must be after start date"); return false;
    }
    return true;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload: any = {
        code: formData.code.trim().toUpperCase(),
        type: formData.type,
        value: formData.type === "FREE_SHIPPING" ? 0 : Number(formData.value),
        minOrderAmount: Number(formData.minOrderAmount),
        maxDiscount: formData.maxDiscount !== "" ? Number(formData.maxDiscount) : null,
        usageLimit: formData.usageLimit !== "" ? Number(formData.usageLimit) : null,
        usagePerUser: formData.usagePerUser !== "" ? Number(formData.usagePerUser) : null,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        isActive: formData.isActive,
        applicableProducts: parseIds(formData.applicableProductsText),
        applicableCategories: parseIds(formData.applicableCategoriasText),
      };

      if (editingCoupon) {
        const res = await couponsApi.updateCoupon(editingCoupon._id, payload);
        toast.success((res as any)?.message || "Coupon updated successfully");
      } else {
        const res = await couponsApi.createCoupon(payload);
        toast.success((res as any)?.message || "Coupon created successfully");
      }
      closeForm();
      fetchCoupons();
    } catch (err) {
      console.error("[Coupons] Submit error:", err);
      toast.error(err instanceof Error ? err.message : "Failed to save coupon");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Disable ────────────────────────────────────────────────────────────────

  const withRowLoading = async (id: string, action: () => Promise<void>) => {
    setActionLoadingIds((prev) => new Set(prev).add(id));
    try { await action(); }
    finally {
      setActionLoadingIds((prev) => {
        const next = new Set(prev); next.delete(id); return next;
      });
    }
  };

  const handleDisable = async (c: Coupon) => {
    const ok = window.confirm(`Disable coupon "${c.code}"? This will deactivate it immediately.`);
    if (!ok) return;
    await withRowLoading(c._id, async () => {
      try {
        const res = await couponsApi.disableCoupon(c._id);
        toast.success((res as any)?.message || "Coupon disabled");
        fetchCoupons();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to disable coupon");
      }
    });
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="mb-1 flex items-center gap-2">
            <Tag className="w-6 h-6 text-primary" />
            Coupons
          </h1>
          <p className="text-muted-foreground">
            Create and manage discount coupons for your store
          </p>
        </div>
        <Button onClick={openCreate} className="w-full sm:w-auto">
          <Plus className="w-4 h-4" />
          Create Coupon
        </Button>
      </div>

      {/* Filters */}
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="relative md:col-span-1">
              <Search className="absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by code…"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 border border-gray-300"
              />
            </div>
            <Select value={typeFilter} onValueChange={handleTypeChange}>
              <SelectTrigger className="border border-gray-300">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                <SelectItem value="FIXED">Fixed</SelectItem>
                <SelectItem value="FREE_SHIPPING">Free Shipping</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger className="border border-gray-300">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(limit)} onValueChange={handleLimitChange}>
              <SelectTrigger className="border border-gray-300">
                <SelectValue placeholder="Per page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="25">25 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Error banner */}
      {fetchError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>⚠ {fetchError}</span>
          <Button variant="ghost" size="sm" onClick={fetchCoupons}>Retry</Button>
        </div>
      )}

      {/* Table */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Coupon List ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-muted-foreground">Loading coupons…</div>
          ) : coupons.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              {fetchError ? "Could not load coupons. Check console for details." : "No coupons found"}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Min Order</TableHead>
                      <TableHead>Usage</TableHead>
                      <TableHead>Products / Categories</TableHead>
                      <TableHead>Valid Till</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {coupons.map((c) => {
                      const rowLoading = actionLoadingIds.has(c._id);
                      const expired = isExpired(c.endDate);
                      return (
                        <TableRow key={c._id}>
                          <TableCell>
                            <span className="font-mono font-semibold tracking-wider">{c.code}</span>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${typeBadgeColor(c.type)}`}
                            >
                              {typeIcon(c.type)}
                              {c.type === "FREE_SHIPPING" ? "Free Ship." : c.type}
                            </span>
                          </TableCell>
                          <TableCell>
                            {c.type === "PERCENTAGE"
                              ? `${c.value}%`
                              : c.type === "FIXED"
                              ? `₹${c.value}`
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {c.minOrderAmount > 0 ? `₹${c.minOrderAmount}` : "—"}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {c.usageCount}
                              {c.usageLimit != null && (
                                <span className="text-muted-foreground">/{c.usageLimit}</span>
                              )}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs space-y-0.5">
                              <div>
                                <span className="text-muted-foreground">P:</span>{" "}
                                {c.applicableProducts?.length > 0
                                  ? `${c.applicableProducts.length} product(s)`
                                  : <span className="text-muted-foreground">All</span>}
                              </div>
                              <div>
                                <span className="text-muted-foreground">C:</span>{" "}
                                {c.applicableCategories?.length > 0
                                  ? `${c.applicableCategories.length} category(s)`
                                  : <span className="text-muted-foreground">All</span>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={expired ? "text-red-500" : ""}>{formatDate(c.endDate)}</span>
                            {expired && (
                              <Badge variant="outline" className="ml-1 text-xs border-red-300 text-red-500">Expired</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={c.isActive ? "default" : "secondary"}>
                              {c.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setViewCoupon(c)}
                                title="View details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEdit(c)}
                                disabled={rowLoading}
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setUsageModalCoupon(c)}
                                title="View usage"
                              >
                                <BarChart2 className="w-4 h-4" />
                              </Button>
                              {c.isActive && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDisable(c)}
                                  disabled={rowLoading}
                                  title="Disable"
                                >
                                  <BanIcon className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <PaginationBar
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* ── View Detail Modal ──────────────────────────────────────────────────── */}
      {viewCoupon && (
        <Dialog open onOpenChange={() => setViewCoupon(null)}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" />
                Coupon Details
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg bg-muted/40 px-4 py-3">
                <span className="font-mono text-xl font-bold tracking-widest">{viewCoupon.code}</span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${typeBadgeColor(viewCoupon.type)}`}>
                  {typeIcon(viewCoupon.type)}{viewCoupon.type}
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {[
                  { label: "Discount Value", value: viewCoupon.type === "PERCENTAGE" ? `${viewCoupon.value}%` : viewCoupon.type === "FIXED" ? `₹${viewCoupon.value}` : "—" },
                  { label: "Min Order", value: viewCoupon.minOrderAmount > 0 ? `₹${viewCoupon.minOrderAmount}` : "None" },
                  { label: "Max Discount", value: viewCoupon.maxDiscount != null ? `₹${viewCoupon.maxDiscount}` : "Unlimited" },
                  { label: "Usage Limit", value: viewCoupon.usageLimit != null ? viewCoupon.usageLimit : "Unlimited" },
                  { label: "Per-User Limit", value: viewCoupon.usagePerUser != null ? viewCoupon.usagePerUser : "Unlimited" },
                  { label: "Used", value: viewCoupon.usageCount },
                  { label: "Start Date", value: formatDate(viewCoupon.startDate) },
                  { label: "End Date", value: formatDate(viewCoupon.endDate) },
                  { label: "Status", value: viewCoupon.isActive ? "Active" : "Inactive" },
                  { label: "Created", value: formatDateTime(viewCoupon.createdAt) },
                ].map((row) => (
                  <React.Fragment key={row.label}>
                    <dt className="text-muted-foreground">{row.label}</dt>
                    <dd className="font-medium">{String(row.value)}</dd>
                  </React.Fragment>
                ))}
              </dl>

              {/* Applicable Products */}
              <div>
                <p className="text-sm font-medium mb-1">Applicable Products</p>
                <IdTagList ids={viewCoupon.applicableProducts ?? []} label="Products" />
              </div>

              {/* Applicable Categories */}
              <div>
                <p className="text-sm font-medium mb-1">Applicable Categories</p>
                <IdTagList ids={viewCoupon.applicableCategories ?? []} label="Categories" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewCoupon(null)}>Close</Button>
              <Button onClick={() => { setViewCoupon(null); openEdit(viewCoupon); }}>
                <Edit className="w-4 h-4 mr-1" /> Edit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Usage Modal ────────────────────────────────────────────────────────── */}
      {usageModalCoupon && (
        <UsageModal coupon={usageModalCoupon} onClose={() => setUsageModalCoupon(null)} />
      )}

      {/* ── Create / Edit Form ─────────────────────────────────────────────────── */}
      <Dialog
        open={isFormOpen}
        onOpenChange={(open) => { if (!open) closeForm(); }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingCoupon ? "Edit Coupon" : "Create Coupon"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

            {/* ── Code ──────────────────────────────────────────────────────── */}
            <div className="space-y-2">
              <Label htmlFor="coupon-code">
                Code <span className="text-red-500">*</span>
              </Label>
              <Input
                id="coupon-code"
                value={formData.code}
                onChange={(e) => setField("code")(e.target.value.toUpperCase())}
                placeholder="SAVE20"
                className="border border-gray-300 font-mono"
                disabled={!!editingCoupon}
              />
              {editingCoupon && (
                <p className="text-xs text-muted-foreground">Code cannot be changed after creation.</p>
              )}
            </div>

            {/* ── Type ──────────────────────────────────────────────────────── */}
            <div className="space-y-2">
              <Label htmlFor="coupon-type">
                Type <span className="text-red-500">*</span>
              </Label>
              <Select value={formData.type} onValueChange={(v) => setField("type")(v as CouponType)}>
                <SelectTrigger id="coupon-type" className="border border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERCENTAGE">Percentage (%)</SelectItem>
                  <SelectItem value="FIXED">Fixed Amount (₹)</SelectItem>
                  <SelectItem value="FREE_SHIPPING">Free Shipping</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* ── Value ─────────────────────────────────────────────────────── */}
            <div className="space-y-2">
              <Label htmlFor="coupon-value">
                Value{" "}
                {formData.type === "PERCENTAGE"
                  ? "(%)"
                  : formData.type === "FIXED"
                  ? "(₹)"
                  : ""}
                {formData.type !== "FREE_SHIPPING" && <span className="text-red-500"> *</span>}
              </Label>
              <Input
                id="coupon-value"
                type="number"
                min={0}
                max={formData.type === "PERCENTAGE" ? 100 : undefined}
                value={formData.type === "FREE_SHIPPING" ? "" : formData.value}
                onChange={(e) => setField("value")(Number(e.target.value))}
                className="border border-gray-300"
                disabled={formData.type === "FREE_SHIPPING"}
                placeholder={formData.type === "FREE_SHIPPING" ? "N/A for Free Shipping" : "20"}
              />
            </div>

            {/* ── Min Order ─────────────────────────────────────────────────── */}
            <div className="space-y-2">
              <Label htmlFor="coupon-min-order">Min Order Amount (₹)</Label>
              <Input
                id="coupon-min-order"
                type="number"
                min={0}
                value={formData.minOrderAmount}
                onChange={(e) => setField("minOrderAmount")(Number(e.target.value))}
                className="border border-gray-300"
                placeholder="0 = no minimum"
              />
            </div>

            {/* ── Max Discount ──────────────────────────────────────────────── */}
            <div className="space-y-2">
              <Label htmlFor="coupon-max-discount">
                Max Discount (₹)
                <span className="ml-1 text-xs text-muted-foreground">optional</span>
              </Label>
              <Input
                id="coupon-max-discount"
                type="number"
                min={0}
                value={formData.maxDiscount}
                onChange={(e) => setField("maxDiscount")(e.target.value)}
                className="border border-gray-300"
                placeholder="Leave blank = no cap"
              />
            </div>

            {/* ── Usage Limit ───────────────────────────────────────────────── */}
            <div className="space-y-2">
              <Label htmlFor="coupon-usage-limit">
                Total Usage Limit
                <span className="ml-1 text-xs text-muted-foreground">optional</span>
              </Label>
              <Input
                id="coupon-usage-limit"
                type="number"
                min={0}
                value={formData.usageLimit}
                onChange={(e) => setField("usageLimit")(e.target.value)}
                className="border border-gray-300"
                placeholder="Leave blank = unlimited"
              />
            </div>

            {/* ── Per-User Limit ────────────────────────────────────────────── */}
            <div className="space-y-2">
              <Label htmlFor="coupon-per-user">
                Per-User Limit
                <span className="ml-1 text-xs text-muted-foreground">optional</span>
              </Label>
              <Input
                id="coupon-per-user"
                type="number"
                min={0}
                value={formData.usagePerUser}
                onChange={(e) => setField("usagePerUser")(e.target.value)}
                className="border border-gray-300"
                placeholder="Leave blank = unlimited"
              />
            </div>

            {/* Spacer for grid alignment */}
            <div />

            {/* ── Start Date ────────────────────────────────────────────────── */}
            <div className="space-y-2">
              <Label htmlFor="coupon-start">
                Start Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="coupon-start"
                type="datetime-local"
                value={formData.startDate}
                onChange={(e) => setField("startDate")(e.target.value)}
                className="border border-gray-300"
              />
            </div>

            {/* ── End Date ──────────────────────────────────────────────────── */}
            <div className="space-y-2">
              <Label htmlFor="coupon-end">
                End Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="coupon-end"
                type="datetime-local"
                value={formData.endDate}
                onChange={(e) => setField("endDate")(e.target.value)}
                className="border border-gray-300"
              />
            </div>

            {/* ── Applicable Products ───────────────────────────────────────── */}
            <div className="space-y-2 md:col-span-2">
              <IdListEditor
                label="Applicable Products"
                hint={"683abc123def456789012345\n683abc123def456789012346"}
                textValue={formData.applicableProductsText}
                onChange={setField("applicableProductsText")}
              />
            </div>

            {/* ── Applicable Categories ─────────────────────────────────────── */}
            <div className="space-y-2 md:col-span-2">
              <IdListEditor
                label="Applicable Categories"
                hint={"683abc123def456789012345\n683abc123def456789012346"}
                textValue={formData.applicableCategoriasText}
                onChange={setField("applicableCategoriasText")}
              />
            </div>

            {/* ── Active toggle ─────────────────────────────────────────────── */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="coupon-active">Active Status</Label>
              <Select
                value={formData.isActive ? "true" : "false"}
                onValueChange={(v) => setField("isActive")(v === "true")}
              >
                <SelectTrigger id="coupon-active" className="border border-gray-300 w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">✅ Active</SelectItem>
                  <SelectItem value="false">⛔ Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={closeForm} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting
                ? "Saving…"
                : editingCoupon
                ? "Update Coupon"
                : "Create Coupon"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
